// RadicalPictogramSelector.jsx
import React from "react";
import { Box, Paper, Stack, TextField, Typography } from "@mui/material";
import { getLetters, PICTOGRAM_LAYOUT_PRESETS } from "./RadicalHelpers";

const PictogramLayoutPreview = React.memo(({ layout, active, onSelect }) => (
    <Paper
        component="button"
        type="button"
        variant="outlined"
        onClick={() => onSelect(layout)}
        sx={{
            p: 1,
            borderRadius: 1.5,
            borderColor: active ? "primary.main" : "divider",
            bgcolor: active ? "action.selected" : "background.paper",
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
            color: "inherit",
            "&:hover": { boxShadow: 2 },
        }}
    >
        <Stack spacing={0.75}>
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    color: active ? "primary.main" : "text.secondary",
                }}
            >
                {layout.name}
            </Typography>

            <Stack spacing={0.5}>
                {layout.rows.map((row, rowIndex) => (
                    <Stack
                        key={`${layout.id}-${rowIndex}`}
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                    >
                        {row.map((letter, cellIndex) => (
                            <Paper
                                key={`${layout.id}-${rowIndex}-${cellIndex}`}
                                variant="outlined"
                                sx={{
                                    width: 38,
                                    minWidth: 38,
                                    height: 44,
                                    px: 0.5,
                                    py: 0.25,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    borderRadius: 1,
                                    borderStyle: letter ? "solid" : "dashed",
                                    bgcolor: letter
                                        ? "background.paper"
                                        : "action.hover",
                                    color: letter
                                        ? "text.primary"
                                        : "text.disabled",
                                    userSelect: "none",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        fontSize: 12,
                                    }}
                                >
                                    {letter || "X"}
                                </Typography>

                                {letter && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            lineHeight: 1.1,
                                            fontSize: 16,
                                        }}
                                    >
                                        {layout.pictograms[letter] || letter}
                                    </Typography>
                                )}
                            </Paper>
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    </Paper>
));

const RadicalPictogramSelector = React.memo(
    ({ packets, pictograms, onPictogramChange, onLayoutSelect }) => {
        const layouts = PICTOGRAM_LAYOUT_PRESETS[packets] || [];
        const activeLayoutId =
            layouts.find((layout) =>
                getLetters(packets).every(
                    (letter) =>
                        (pictograms[letter] || "") ===
                        (layout.pictograms[letter] || ""),
                ),
            )?.id || "";

        return (
            <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Pictogramas para {packets} paquetes
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    {getLetters(packets).map((letter) => (
                        <Box
                            key={letter}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {letter}:
                            </Typography>

                            <TextField
                                value={pictograms[letter] || ""}
                                onChange={(event) =>
                                    onPictogramChange(
                                        letter,
                                        event.target.value,
                                    )
                                }
                                size="small"
                                variant="standard"
                                placeholder={letter}
                                sx={{
                                    width: 42,
                                    "& .MuiInputBase-input": {
                                        textAlign: "center",
                                        fontSize: 18,
                                        py: 0.25,
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Box>

                {layouts.length > 0 && (
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.75 }}
                        >
                            Posiciones en mesa. Haz clic para usar esa
                            disposición.
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                            }}
                        >
                            {layouts.map((layout) => (
                                <PictogramLayoutPreview
                                    key={layout.id}
                                    layout={layout}
                                    active={layout.id === activeLayoutId}
                                    onSelect={onLayoutSelect}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Stack>
        );
    },
);

export default RadicalPictogramSelector;
